// TODO: Remove this when fixing the unit tests - BTCOBUI-2597
/* istanbul ignore file */
import { useMediaQuery } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { overviewWidgetsPermissionsArray } from '../../App.util';
import { LocalStorageKey } from '../../constants/localStorageKey';
import { StoreDetails } from '../../global-state/types';
import { useAsyncAction, useLocalStorage, useUser } from '../../hooks';
import { PermissionItemsMap, useUserPermissions } from '../../hooks/useUserPermissions';
import { Icon, Icons, Typography } from '../../stories/atoms';
import { ColumnFlexContainer, ContainerV2, RowFlexContainer } from '../../stories/atoms/containerV2/ContainerV2';
import LocationActiveIcon from '../../stories/atoms/icon/assets/LocationActive.svg';
import LocationActiveHM from '../../stories/atoms/icon/assets/LocationActive_HM.svg';
import LocationIcon from '../../stories/atoms/icon/assets/LocationOthers.svg';
import LocationHM from '../../stories/atoms/icon/assets/Location_HM.svg';
import { Animation, Animations, Button, IconWithText } from '../../stories/molecules';
import { InputSelect } from '../../stories/molecules/inputSelect/InputSelect';
import { Search } from '../../stories/molecules/search/Search';
import { Map } from '../../stories/organisms';
import { muiTheme } from '../../theme';
import { commonTranslations, selectStoreTranslation } from '../../translations';
import { getUserPermissions, isFailureResponse } from '../../utils';
import { StyledDropdowns, StyledScreenContainer } from './SelectStore.styles';

const {
  searchInputPlaceHolder,
  selectArea,
  storeIdText,
  findStore,
  storeSelectionText,
  storeStatus,
  selectStore,
  loadingPermissionsError,
} = selectStoreTranslation;

const { errorText1, reset } = commonTranslations;

type DropdownType = 'area' | 'store';

const dropdownConfigs: {
  type: DropdownType;
  labelKey: string;
  defaultValue: string;
}[] = [
  { type: 'area', labelKey: selectArea.key, defaultValue: selectArea.defaultValue },
  { type: 'store', labelKey: selectStore.key, defaultValue: selectStore.defaultValue },
];

export const SelectStore: FC = observer(() => {
  const {
    get: { storesDetails, previousStoreId, profile },
    set,
  } = useUser();

  const { t } = useTranslation();
  const { setItem } = useLocalStorage();
  const navigate = useNavigate();
  const isMobileView = useMediaQuery(muiTheme.breakpoints.down('tabletLandscape'));
  const { fetchUserPermissions } = useUserPermissions();

  const [selectedStoreId, setSelectedStoreId] = useState<string>('');
  const [selectedStoreInfo, setSelectedStoreInfo] = useState<StoreDetails | null>(null);
  const [selectedArea, setSelectedArea] = useState<string>('');
  const [canViewArea, setCanViewArea] = useState(false);
  const [permissionNetworkError, setPermissionNetworkError] = useState(false);

  const {
    result: permissionsDetails,
    isLoading: permissionsDetailsLoading,
    isError: permissionsDetailsError,
    triggerAction: triggerFetchPermissionsDetails,
    reset: resetPermissionsDetails,
  } = useAsyncAction<PermissionItemsMap>();

  const fetchPermissions = async (storeId: string) => {
    const response = await fetchUserPermissions(profile.upn, storeId);

    if (isFailureResponse(response)) {
      if (response.status === 404) {
        setPermissionNetworkError(true);
        resetPermissionsDetails();
      }
      throw new Error();
    }

    setPermissionNetworkError(false);
    const permissions = getUserPermissions(response.data);
    const hasOverviewPermissions = overviewWidgetsPermissionsArray.some((widget) => permissions[widget]?.canRead);
    setCanViewArea(hasOverviewPermissions);

    return response.data;
  };

  useEffect(() => {
    const checkPermissions = async () => {
      if (!selectedStoreId && selectedArea) {
        const areaStores = storesDetails.filter((store) => store.area === selectedArea);
        const areaStoreId = areaStores.length > 0 ? areaStores[0].storeId : null;

        if (areaStoreId) {
          await triggerFetchPermissionsDetails(() => fetchPermissions(areaStoreId));
        }
      } else if (selectedStoreId) {
        await triggerFetchPermissionsDetails(() => fetchPermissions(selectedStoreId));
      }
    };

    checkPermissions();
  }, [selectedStoreId, selectedArea]);

  const setSelectedMapStore = (store: StoreDetails) => {
    if (store) {
      setSelectedStoreId(store.storeId);
      setSelectedStoreInfo(store);
      setSelectedArea(store.area);
    }
  };

  const handleSearch = (selectedValue: StoreDetails | null) => {
    if (selectedValue) {
      setSelectedMapStore(selectedValue);
    } else {
      setSelectedStoreId('');
      setSelectedStoreInfo(null);
      setSelectedArea('');
    }
  };

  const customFilterFunction = (options: StoreDetails[], inputValue: string) => {
    return options.filter(
      (option) =>
        option.storeId.toLowerCase().includes(inputValue.toLowerCase()) ||
        option.name.toLowerCase().includes(inputValue.toLowerCase()),
    );
  };

  const handleMarkerClick = async (storeId: string) => {
    const storeMarker = storesDetails?.find((storeDetails) => storeDetails.storeId === storeId);

    if (!storeMarker) {
      return;
    }

    setSelectedMapStore(storeMarker);
  };

  const getLocationMarkerIcon = (storeId?: string) => {
    const targetStoreDetails = storesDetails?.find((storeDetails) => storeDetails.storeId === storeId);

    let corporateBrandId = 0;

    if (targetStoreDetails) {
      corporateBrandId = targetStoreDetails.corporateBrandId;
    }

    const isActive = selectedStoreId && selectedStoreId === storeId;

    let location;

    if (corporateBrandId === 0) {
      location = isActive ? LocationActiveHM : LocationHM;
    } else {
      location = isActive ? LocationActiveIcon : LocationIcon;
    }

    return location;
  };

  const navigateToPreviousSelectedStore = () => {
    if (previousStoreId) {
      setItem(LocalStorageKey.SelectedStore, previousStoreId);
      set({ currentStoreId: previousStoreId });

      navigate('/');
    }
  };

  const getUniqueValues = (arr: string[]) => Array.from(new Set(arr.filter(Boolean)));

  const areaOptions = ['All', ...getUniqueValues(storesDetails.map((store) => store.area))];

  const isAllAreas = selectedArea === 'All';

  const storeOptions = getUniqueValues(
    storesDetails
      .filter((store) => isAllAreas || !selectedArea || store.area?.trim() === selectedArea.trim())
      .map((store) => (store.name ? `${store.storeId} - ${store.name}` : store.storeId)),
  );

  const menuItemsByType: Record<DropdownType, string[]> = {
    area: areaOptions,
    store: storeOptions,
  };

  const handleDropDownChange = (type: DropdownType) => (selectedValues: string | string[] | null) => {
    const value = Array.isArray(selectedValues) ? selectedValues[0] : selectedValues;

    if (value) {
      switch (type) {
        case 'area':
          setSelectedArea(value.trim());
          setSelectedStoreId('');
          setSelectedStoreInfo(null);
          setPermissionNetworkError(false);
          break;
        case 'store': {
          setPermissionNetworkError(false);
          const storeId = value.includes(' - ') ? value.split(' - ')[0] : value;
          const matchedStore = storesDetails.find((store) => store.storeId === storeId);

          if (matchedStore) {
            setSelectedArea(matchedStore.area || '');
            setSelectedStoreId(matchedStore.storeId);
            setSelectedStoreInfo(matchedStore);
          }
          break;
        }
      }
    }
  };

  const getSelectedValue = (type: DropdownType) => {
    if (type === 'area') return selectedArea;
    if (type === 'store') {
      const selected = storesDetails.find((store) => store.storeId === selectedStoreId);
      return selected ? (selected.name ? `${selected.storeId} - ${selected.name}` : selected.storeId) : '';
    }
    return '';
  };

  // TODO: Update this when the area overview page is ready - BTCOBUI-3305
  const onStoreSelect = () => {
    setItem(LocalStorageKey.SelectedStore, selectedStoreId as string);
    setItem(LocalStorageKey.SelectedView, 'store' as string);
    set({ currentStoreId: selectedStoreId });
    navigate('/');
  };

  const onAreaSelect = () => {
    const areaSelectedStoreId = storesDetails.filter((store) => store.area === selectedArea)[0]?.storeId || '';
    setItem(LocalStorageKey.SelectedStore, areaSelectedStoreId as string);
    setItem(LocalStorageKey.SelectedView, 'area' as string);
    set({ currentStoreId: areaSelectedStoreId });
    navigate('/home');
  };

  // Dropdown filtering
  const filteredStores = storesDetails.filter((store) => {
    const matchesArea = isAllAreas || !selectedArea || store.area?.trim() === selectedArea.trim();
    const matchesStore = !selectedStoreId || store.storeId === selectedStoreId;

    return matchesArea && matchesStore;
  });

  const markers = useMemo(
    () =>
      storesDetails
        .filter((store) => {
          const matchesArea = isAllAreas || !selectedArea || store.area?.trim() === selectedArea.trim();
          return matchesArea;
        })
        .map(({ storeId, latitude, longitude }) => ({
          id: storeId,
          latitude: Number.parseFloat(latitude),
          longitude: Number.parseFloat(longitude),
        })),
    [isAllAreas, selectedArea],
  );

  return (
    <StyledScreenContainer>
      <RowFlexContainer padding={[6]} justifyContent="between" width="100%">
        <IconWithText label="Store Performance" iconOptions={{ icon: Icons.BOX, size: 'large' }} />
        <Icon icon={Icons.HM_LOGO} size="large" onClick={navigateToPreviousSelectedStore} />
      </RowFlexContainer>

      <RowFlexContainer padding={[0, 0, 5, 0]} data-testid="select-store-container" height="100%">
        {!isMobileView && (
          <ColumnFlexContainer data-testid="map-container" height="80vh" width="65vw" padding={[6]} gap={[2]}>
            <Search
              fieldLabel={t(searchInputPlaceHolder.key, searchInputPlaceHolder.defaultValue)}
              options={filteredStores}
              getOptionLabel={(store) => `${store.storeId} - ${store.name}`}
              onSelect={handleSearch}
              filterFunction={customFilterFunction}
              onClear={() => {
                setCanViewArea(false);
                resetPermissionsDetails();
                setPermissionNetworkError(false);
              }}
              markerOption={
                selectedStoreId
                  ? storesDetails?.find((storeDetails) => storeDetails.storeId === selectedStoreId)
                  : null
              }
            />
            <Map
              zoom={2}
              selectedMarkerId={selectedStoreId || ''}
              markers={markers}
              prefferedProvider="Google"
              panOnMarkerSelect
              onMarkerSelectZoom={12}
              onMarkerClick={handleMarkerClick}
              getMarkerIconUrl={getLocationMarkerIcon}
            />
          </ColumnFlexContainer>
        )}

        <ColumnFlexContainer data-testid="select-store-dropdowns" padding={[4, 6]} flex={1}>
          {isMobileView && (
            <Search
              fieldLabel={t(searchInputPlaceHolder.key, searchInputPlaceHolder.defaultValue)}
              options={filteredStores}
              getOptionLabel={(store) => `${store.storeId} - ${store.name}`}
              onSelect={handleSearch}
              filterFunction={customFilterFunction}
              onClear={() => {
                setCanViewArea(false);
                resetPermissionsDetails();
                setPermissionNetworkError(false);
              }}
              markerOption={
                selectedStoreId
                  ? storesDetails?.find((storeDetails) => storeDetails.storeId === selectedStoreId)
                  : null
              }
            />
          )}
          <Typography type="h4" margin={isMobileView ? [4, 0] : [0, 0, 4, 0]}>
            {t(findStore.key, findStore.defaultValue)}
          </Typography>
          <Typography gutterBottom padding={!isMobileView ? [0, 0, 4, 0] : undefined}>
            {t(storeSelectionText.key, storeSelectionText.defaultValue, {
              numberOfStores: storesDetails?.length,
            })}
          </Typography>

          {!permissionsDetailsLoading && permissionsDetailsError && permissionNetworkError && (
            <RowFlexContainer
              backgroundColor="backgroundWarning"
              padding={[2, 3]}
              borderRadius={[2]}
              alignItems="center"
              margin={[0, 0, 2, 0]}
            >
              <Icon icon={Icons.WARNING} color="warningLight" size="medium" margin={[0, 2, 0, 0]} />
              <Typography type="caption">
                {t(
                  selectStoreTranslation.permissionNetworkError.key,
                  selectStoreTranslation.permissionNetworkError.defaultValue,
                )}
              </Typography>
            </RowFlexContainer>
          )}

          {!permissionsDetailsLoading && permissionsDetailsError && !permissionNetworkError && (
            <Animation
              title={t(errorText1.key, errorText1.defaultValue)}
              subtitle={t(loadingPermissionsError.key, loadingPermissionsError.defaultValue)}
              animation={Animations.SAD}
              size="small"
              customWrapperStyle={{ height: 'auto' }}
              button={{
                label: t(reset.key, reset.defaultValue),
                onClick: () => {
                  setSelectedArea('All');
                  setSelectedStoreId('');
                  setSelectedStoreInfo(null);
                  resetPermissionsDetails();
                  setPermissionNetworkError(false);
                },
                buttonContentProps: {
                  iconOptions: {
                    icon: Icons.REFRESH,
                    color: 'white',
                  },
                },
              }}
            />
          )}
          {((!permissionsDetailsError && !permissionNetworkError) ||
            (permissionsDetailsError && permissionNetworkError) ||
            permissionsDetailsLoading) &&
            dropdownConfigs.map(({ type, labelKey, defaultValue }) => (
              <StyledDropdowns key={type}>
                <InputSelect
                  label={t(labelKey, defaultValue)}
                  placeholder={t(labelKey, defaultValue)}
                  menuItems={menuItemsByType[type]}
                  value={getSelectedValue(type)}
                  onChange={handleDropDownChange(type)}
                  isFullWidth
                />

                <Button
                  variant="primary"
                  data-testid={`select-${type}-button`}
                  label={t(labelKey, defaultValue)}
                  onClick={type === 'area' ? onAreaSelect : onStoreSelect}
                  isLoading={permissionsDetailsLoading}
                  disabled={
                    permissionsDetails === null ||
                    permissionsDetailsLoading ||
                    (type === 'area' && getSelectedValue(type) === 'All') ||
                    (type === 'area' ? !canViewArea : !selectedStoreId)
                  }
                  buttonContentProps={{
                    textOptions: {
                      whiteSpace: 'nowrap',
                    },
                  }}
                />
              </StyledDropdowns>
            ))}

          {!permissionsDetailsLoading &&
            !permissionsDetailsError &&
            !permissionNetworkError &&
            selectedStoreInfo && (
              <ContainerV2 data-testid={`selected-store-details-${isMobileView ? 'mobile' : 'desktop'}`}>
                {selectedArea && !isAllAreas && <Typography type="body4">{selectedArea}</Typography>}
                <Typography type="body3">{selectedStoreInfo.name}</Typography>
                <Typography>{selectedStoreInfo.address}</Typography>
                <Typography>
                  {t(storeIdText.key, storeIdText.defaultValue)} - {selectedStoreInfo.storeId}
                </Typography>
                {selectedStoreInfo.status && (
                  <RowFlexContainer>
                    <Typography>{t(storeStatus.key, storeStatus.defaultValue)} - </Typography>
                    <Typography gutterBottom color={selectedStoreInfo.status === 'OPEN' ? 'primary' : 'red'}>
                      {selectedStoreInfo.status}
                    </Typography>
                  </RowFlexContainer>
                )}
              </ContainerV2>
            )}
        </ColumnFlexContainer>
      </RowFlexContainer>
    </StyledScreenContainer>
  );
});
